/**
 * Constructor.io front-end integration: header autocomplete and the search results page.
 *
 * - Results come from Constructor through its JavaScript client library. The beacon loaded in theme.liquid
 *   (cnstrc.com/js/cust/…) bundles that library as window.ConstructorioClient, so nothing else is loaded. A second
 *   instance shares the beacon's i/s identifiers because both read the same storage.
 * - Product cards are still rendered by Shopify (sections/cnstrc-results.liquid) so they keep swatches, quick buy,
 *   market prices and their data-cnstrc-* tracking attributes.
 * - Behavioural tracking stays with the beacon, which reads the data-cnstrc-* attributes set here.
 * - If Constructor cannot be reached, both fall back to Shopify search.
 */
(function () {
  "use strict";

  const CARDS_SECTION = "cnstrc-results";
  const CARDS_BATCH = 12; // all_products allows 20 unique handles per render

  // ── Helpers ────────────────────────────────────────────────────────────────

  let clientPromise = null;

  function getClient() {
    if (!clientPromise) {
      clientPromise = new Promise((resolve, reject) => {
        const startedAt = Date.now();

        (function waitForBeacon() {
          if (window.ConstructorioClient) {
            const cnstrc = window.cnstrc || {};
            const options = { apiKey: cnstrc.indexKey };
            if (cnstrc.userId) options.userId = cnstrc.userId;
            return resolve(new window.ConstructorioClient(options));
          }
          if (Date.now() - startedAt > 5000) return reject(new Error("Constructor client did not load"));
          setTimeout(waitForBeacon, 50);
        })();
      });
      clientPromise.catch(() => (clientPromise = null));
    }
    return clientPromise;
  }

  function escapeHtml(value) {
    return String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  }

  function handleize(value) {
    return String(value).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  }

  function handleFromUrl(url) {
    try {
      const match = new URL(url, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (_) {
      return null;
    }
  }

  function searchUrl(params) {
    return `${window.Shopify.routes.root}search?${params.toString()}`;
  }

  async function fetchHtml(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`${response.status} ${url}`);
    return new DOMParser().parseFromString(await response.text(), "text/html");
  }

  // Renders products in Constructor's order with the theme's cards. Returns the nodes inside the section wrapper.
  async function renderCards(mode, handles, signal) {
    const batches = [];
    for (let i = 0; i < handles.length; i += CARDS_BATCH) {
      batches.push({ offset: i, handles: handles.slice(i, i + CARDS_BATCH) });
    }

    const docs = await Promise.all(batches.map((batch) => {
      const params = new URLSearchParams({ q: `cnstrc:${mode}:${batch.offset}:${batch.handles.join(",")}`, section_id: CARDS_SECTION });
      return fetchHtml(searchUrl(params), signal);
    }));

    return docs.flatMap((doc) => Array.from(doc.querySelector(".shopify-section")?.childNodes || []));
  }

  function loadingStart() {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
  }

  function loadingEnd() {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
  }

  // ── Header autocomplete ────────────────────────────────────────────────────

  /**
   * Replaces the theme's <predictive-search>. Same markup and behaviour, but suggestions and products come from
   * Constructor's autocomplete endpoint.
   */
  class CnstrcPredictiveSearch extends HTMLElement {
    #abortController;
    #listenersAbortController;
    #form;
    #input;
    #debounceTimer;

    connectedCallback() {
      this.#listenersAbortController = new AbortController();
      const signal = this.#listenersAbortController.signal;

      this.#form = document.querySelector(`[aria-owns="${this.id}"]`);
      this.#input = this.#form.elements["q"];

      this.#form.addEventListener("submit", (event) => {
        if (this.#input.value.trim() === "") event.preventDefault();
      }, { signal });
      this.#form.addEventListener("reset", () => this.#clear(), { signal });
      this.#input.addEventListener("input", () => {
        clearTimeout(this.#debounceTimer);
        this.#debounceTimer = setTimeout(() => this.#onInputChanged(), 280);
      }, { signal });
    }

    disconnectedCallback() {
      this.#listenersAbortController.abort();
      this.#abortController?.abort();
    }

    get #results() {
      return this.querySelector('[slot="results"]');
    }

    #onInputChanged() {
      const term = this.#input.value.trim();
      if (term === "") return this.#clear();

      this.#abortController?.abort();
      this.#abortController = new AbortController();
      this.#search(term, this.#abortController.signal).catch((error) => {
        if (error.name !== "AbortError") console.error(error);
      });
    }

    async #search(term, signal) {
      loadingStart();

      try {
        let content;
        try {
          content = await this.#constructorResults(term, signal);
        } catch (error) {
          if (error.name === "AbortError") throw error;
          console.warn("Constructor autocomplete failed, using Shopify predictive search", error);
          content = await this.#shopifyResults(term, signal);
        }

        if (!signal.aborted) this.#results.replaceChildren(...content);
      } finally {
        loadingEnd();
      }
    }

    async #constructorResults(term, signal) {
      const client = await getClient();
      const response = await client.autocomplete.getAutocompleteResults(term, {
        resultsPerSection: { Products: 5, "Search Suggestions": 8 }
      });
      if (signal.aborted) throw new DOMException("Aborted", "AbortError");

      const suggestions = (response.sections["Search Suggestions"] || []).map((item) => item.value);
      const handles = (response.sections["Products"] || []).map((item) => handleFromUrl(item.data?.url)).filter(Boolean);

      if (suggestions.length === 0 && handles.length === 0) {
        const message = document.createElement("p");
        message.className = "predictive-search__no-results text-lg";
        message.textContent = this.dataset.labelNoResults.replace("__TERMS__", term);
        return [message];
      }

      // Same rule as sections/predictive-search.liquid: 4 products next to suggestions, 5 otherwise
      const productNodes = handles.length > 0 ? await renderCards("predictive", handles.slice(0, suggestions.length > 0 ? 4 : 5), signal) : [];
      const wrapper = document.createElement("div");
      wrapper.append(...productNodes);

      let results = wrapper.querySelector(".predictive-search__results");
      if (!results) {
        results = document.createElement("div");
        results.className = "predictive-search__results";
      }

      if (suggestions.length > 0) {
        results.classList.add("predictive-search__results--with-suggestions");
        results.insertAdjacentHTML("afterbegin", this.#suggestionsHtml(suggestions));
      }

      return [results];
    }

    #suggestionsHtml(suggestions) {
      const items = suggestions.map((suggestion) => {
        const url = searchUrl(new URLSearchParams({ q: suggestion }));
        return `
          <div data-cnstrc-item-section="Search Suggestions" data-cnstrc-item-name="${escapeHtml(suggestion)}">
            <a href="${escapeHtml(url)}" class="link-reverse" data-instant>${escapeHtml(suggestion)}</a>
          </div>`;
      }).join("");

      return `
        <div class="predictive-search__resource-item">
          <div class="v-stack gap-6 sm:gap-8">
            <p class="predictive-search__category h6 text-subdued">${escapeHtml(this.dataset.labelSuggestions)}</p>
            <div class="predictive-search__suggestions scroll-area bleed md:unbleed">${items}</div>
          </div>
        </div>`;
    }

    // Same request as the theme's own predictive search
    async #shopifyResults(term, signal) {
      const params = new URLSearchParams({ q: term, section_id: "predictive-search", "resources[limit]": 10, "resources[limit_scope]": "each" });
      const doc = await fetchHtml(`${window.Shopify.routes.root}search/suggest?${params.toString()}`, signal);
      return Array.from(document.importNode(doc.querySelector(".shopify-section"), true).children);
    }

    #clear() {
      clearTimeout(this.#debounceTimer);
      this.#abortController?.abort();
      this.#input.focus();
      this.#results.innerHTML = "";
    }
  }

  if (!window.customElements.get("cnstrc-predictive-search")) {
    window.customElements.define("cnstrc-predictive-search", CnstrcPredictiveSearch);
  }

  // ── Search results page ────────────────────────────────────────────────────

  /**
   * State lives in the URL, in the same shape the theme's facet components already produce:
   *   ?q=…&page=2&sort_by=<sort_by>-<sort_order>&filter.<facet>=<value>&filter.v.price.gte=…&filter.v.price.lte=…
   */
  function stateFromUrl(url, term) {
    const params = new URL(url, window.location.origin).searchParams;
    const filters = {};

    params.forEach((value, key) => {
      if (!key.startsWith("filter.") || key.startsWith("filter.v.price.") || value === "") return;
      const name = key.slice("filter.".length);
      (filters[name] ||= []).push(value);
    });

    const priceMin = params.get("filter.v.price.gte");
    const priceMax = params.get("filter.v.price.lte");
    if (priceMin || priceMax) {
      filters.price = [`${priceMin || 0}-${priceMax || "inf"}`];
    }

    return {
      term,
      page: Math.max(1, parseInt(params.get("page"), 10) || 1),
      sort: params.get("sort_by") || "",
      filters,
      priceMin,
      priceMax
    };
  }

  function urlFromState(state, overrides = {}) {
    const next = { ...state, ...overrides };
    const params = new URLSearchParams({ q: next.term });

    if (next.page > 1) params.set("page", next.page);
    if (next.sort) params.set("sort_by", next.sort);

    Object.entries(next.filters).forEach(([name, values]) => {
      if (name === "price") return;
      values.forEach((value) => params.append(`filter.${name}`, value));
    });

    if (next.filters.price) {
      if (next.priceMin) params.set("filter.v.price.gte", next.priceMin);
      if (next.priceMax) params.set("filter.v.price.lte", next.priceMax);
    }

    return searchUrl(params);
  }

  class CnstrcSearch extends HTMLElement {
    #settings;
    #state;
    #abortController;
    #openFacets = new Set();
    #facetsInitialized = false;
    #overlays = [];

    connectedCallback() {
      this.#settings = JSON.parse(this.querySelector("script[data-cnstrc-settings]").textContent);
      this.#state = stateFromUrl(window.location.href, this.getAttribute("term"));

      // The facets drawer and sort popover are moved to <body> while open, so keep references to query them
      this.#overlays = Array.from(this.querySelectorAll("facets-drawer, facets-sort-popover"));

      // Answer the theme's facet:update events (filters, sort, pagination, chips) before facets-listeners.js does.
      // Capture on window runs first. Matched by section id, as the drawer may sit outside this element.
      this._onFacetUpdate = (event) => {
        const url = event.detail?.url;
        if (!url || url.searchParams.get("section_id") !== this.getAttribute("section-id")) return;
        event.stopImmediatePropagation();
        this.#state = stateFromUrl(url, this.#state.term);
        this.#update({ scroll: true });
      };
      window.addEventListener("facet:update", this._onFacetUpdate, true);

      this.addEventListener("toggle", this.#onToggle, true);
      this.#overlays.forEach((overlay) => overlay.addEventListener("toggle", this.#onToggle, true));

      this.#update({ scroll: false, initial: true });
    }

    #onToggle = (event) => {
      const facet = event.target.closest?.("[data-cnstrc-facet]")?.dataset.cnstrcFacet;
      if (facet) event.target.open ? this.#openFacets.add(facet) : this.#openFacets.delete(facet);
    };

    #queryAll(selector) {
      const elements = new Set(this.querySelectorAll(selector));
      this.#overlays.forEach((overlay) => overlay.querySelectorAll(selector).forEach((element) => elements.add(element)));
      return Array.from(elements);
    }

    disconnectedCallback() {
      window.removeEventListener("facet:update", this._onFacetUpdate, true);
      this.#abortController?.abort();
    }

    get #collection() {
      return this.querySelector(".collection");
    }

    async #update({ scroll, initial = false }) {
      this.#abortController?.abort();
      this.#abortController = new AbortController();
      const signal = this.#abortController.signal;
      const state = this.#state;

      // Like facets-listeners.js; skipped on load so other parameters (utm_*, preview) stay in the URL
      if (!initial) history.replaceState({}, "", urlFromState(state));

      // Constructor: num-results / result-id must not be present while a request is in progress
      this.setAttribute("aria-busy", "true");
      this.#collection.removeAttribute("data-cnstrc-num-results");
      this.#collection.removeAttribute("data-cnstrc-result-id");
      loadingStart();

      try {
        const data = await this.#fetchConstructor(state);
        if (signal.aborted) return;

        const redirectUrl = data.response?.redirect?.data?.url;
        if (redirectUrl) {
          window.location.replace(redirectUrl);
          return;
        }

        const results = data.response?.results || [];
        const handles = results.map((item) => handleFromUrl(item.data?.url)).filter(Boolean);
        const cards = handles.length > 0 ? await renderCards("grid", handles, signal) : [];
        if (signal.aborted) return;

        this.#render(data, cards);
        this.#collection.setAttribute("data-cnstrc-result-page", state.page);
        this.#collection.setAttribute("data-cnstrc-result-id", data.result_id);
        this.#collection.setAttribute("data-cnstrc-num-results", data.response?.total_num_results || 0);
      } catch (error) {
        if (error.name === "AbortError") return;
        console.warn("Constructor search failed, using Shopify search", error);
        await this.#renderShopifyFallback(signal).catch(() => {});
      } finally {
        if (!signal.aborted) {
          this.setAttribute("aria-busy", "false");
          loadingEnd();
          if (scroll) this.#collection.scrollIntoView({ block: "start", behavior: "smooth" });
        }
      }
    }

    async #fetchConstructor(state) {
      const client = await getClient();
      const parameters = {
        page: state.page,
        resultsPerPage: parseInt(this.getAttribute("per-page"), 10) || 24,
        filters: state.filters
      };

      if (state.sort) {
        const index = state.sort.lastIndexOf("-");
        parameters.sortBy = state.sort.slice(0, index);
        parameters.sortOrder = state.sort.slice(index + 1);
      }

      return client.search.getSearchResults(state.term, parameters);
    }

    #render(data, cards) {
      const response = data.response || {};
      const total = response.total_num_results || 0;
      const facets = this.#settings.showFilters ? (response.facets || []).filter((facet) => this.#isVisibleFacet(facet)) : [];
      const activeCount = this.#activeFilters(facets).length;

      this.toggleAttribute("has-facets", facets.length > 0 && (total > 0 || activeCount > 0));
      this.toggleAttribute("has-results", total > 0 || activeCount > 0);

      this.querySelector("[data-cnstrc-count]").textContent = this.#countText(total);
      const activeCountElement = this.querySelector("[data-cnstrc-active-count]");
      if (activeCountElement) activeCountElement.textContent = activeCount > 0 ? `(${activeCount})` : "";

      this.#renderFacets(facets);
      this.#renderSort(response.sort_options || []);
      this.querySelector("[data-cnstrc-active-facets]").innerHTML = this.#activeFacetsHtml(facets);
      this.querySelector("product-list").replaceChildren(...cards);
      this.querySelector("[data-cnstrc-pagination]").innerHTML = this.#paginationHtml(total);

      const empty = this.querySelector("[data-cnstrc-empty]");
      empty.hidden = total > 0;
      empty.innerHTML = total > 0 ? "" : this.#emptyHtml(activeCount > 0);
    }

    #isVisibleFacet(facet) {
      if (facet.hidden) return false;
      if (facet.type === "range") return facet.name === "price" && this.#settings.priceFilter && facet.max > facet.min;
      return (facet.options || []).length > 0;
    }

    #countText(total) {
      const template = total === 1 ? this.#settings.strings.resultsCountOne : this.#settings.strings.resultsCount;
      return template.replace("__COUNT__", total).replace("__TERMS__", this.#state.term);
    }

    #icon(name) {
      return this.querySelector(`template[data-cnstrc-icon="${name}"]`)?.innerHTML || "";
    }

    // ── Facets (same markup as snippets/facets.liquid, accordion.liquid, checkbox.liquid, price-range.liquid)

    #renderFacets(facets) {
      if (!this.#facetsInitialized) {
        if (this.#settings.openFilters) facets.forEach((facet) => this.#openFacets.add(facet.name));
        this.#facetsInitialized = true;
      }

      this.#queryAll("[data-cnstrc-facets]").forEach((container) => {
        container.innerHTML = facets.length > 0 ? this.#facetsFormHtml(facets, container.dataset.cnstrcFacets) : "";
      });
    }

    #facetsFormHtml(facets, context) {
      const sectionId = this.getAttribute("section-id");
      const sortInput = this.#state.sort ? `<input type="hidden" name="sort_by" value="${escapeHtml(this.#state.sort)}">` : "";

      return `
        <facets-form section-id="${sectionId}" update-on-change>
          <form method="GET" action="${escapeHtml(window.Shopify.routes.root)}search" class="facets" update-on-change>
            <input type="hidden" name="page" value="">
            <input type="hidden" name="q" value="${escapeHtml(this.#state.term)}">
            ${sortInput}
            <div class="accordion-list">
              ${facets.map((facet) => this.#accordionHtml(facet, this.#facetContentHtml(facet, `facets-${context}`))).join("")}
            </div>
          </form>
        </facets-form>`;
    }

    #accordionHtml(facet, content) {
      const title = escapeHtml(facet.display_name);
      const open = this.#openFacets.has(facet.name);

      return `
        <accordion-disclosure class="accordion nacc_${handleize(facet.display_name)} ${title}" id="acc_${title}" data-cnstrc-facet="${escapeHtml(facet.name)}">
          <details class="accordion__disclosure group" ${open ? 'open aria-expanded="true"' : 'aria-expanded="false"'}>
            <summary>
              <span class="accordion__toggle h6">
                <span class="text-with-icon gap-4">${title}</span>
                <div class="facets_ic">${this.#icon("chevron-down")}</div>
              </span>
            </summary>
            <div class="accordion__content">${content}</div>
          </details>
        </accordion-disclosure>`;
    }

    #facetContentHtml(facet, idPrefix) {
      if (facet.type === "range") return this.#priceRangeHtml(facet);

      const sectionId = this.getAttribute("section-id");
      const name = `filter.${facet.name}`;

      const options = facet.options.map((option, index) => {
        const id = `checkbox-${idPrefix}-${index + 1}-${sectionId}--${handleize(name)}-${handleize(option.value)}`;
        const label = this.#settings.showValuesCount ? `${option.display_name} (${option.count})` : option.display_name;

        return `
          <div class="checkbox-control">
            <input id="${id}" class="dot-checkbox" type="checkbox" name="${escapeHtml(name)}" value="${escapeHtml(option.value)}" ${option.status === "selected" ? "checked" : ""}>
            <label for="${id}">${escapeHtml(label)}</label>
          </div>`;
      }).join("");

      return `<div class="v-stack gap-2.5">${options}</div>`;
    }

    #priceRangeHtml(facet) {
      const strings = this.#settings.strings;
      const symbol = escapeHtml(this.#settings.currencySymbol);
      const rangeMax = Math.ceil(facet.max);
      const minValue = this.#state.priceMin ? Math.ceil(this.#state.priceMin) : "";
      const maxValue = this.#state.priceMax ? Math.ceil(this.#state.priceMax) : "";
      const lower = ((minValue || 0) / rangeMax) * 100;
      const higher = ((maxValue || rangeMax) / rangeMax) * 100;

      return `
        <price-range class="price-range">
          <div class="range-group" style="--range-min: ${lower}%; --range-max: ${higher}%">
            <input type="range" aria-label="${escapeHtml(strings.priceFrom)}" class="range" min="0" max="${rangeMax}" value="${minValue || 0}">
            <input type="range" aria-label="${escapeHtml(strings.priceTo)}" class="range" min="0" max="${rangeMax}" value="${maxValue || rangeMax}">
          </div>
          <div class="input-group">
            <label class="input-prefix text-sm">
              <span class="prefix text-subdued">${symbol}</span>
              <input aria-label="${escapeHtml(strings.priceFrom)}" class="field" type="number" inputmode="numeric" value="${minValue}" name="filter.v.price.gte" min="0" max="${maxValue || rangeMax}" placeholder="0" autocomplete="off">
            </label>
            <span class="text-subdued text-sm">${escapeHtml(strings.priceRangeTo)}</span>
            <label class="input-prefix text-sm">
              <span class="prefix text-subdued">${symbol}</span>
              <input aria-label="${escapeHtml(strings.priceTo)}" class="field" type="number" inputmode="numeric" value="${maxValue}" name="filter.v.price.lte" min="${minValue || 0}" max="${rangeMax}" placeholder="${rangeMax}" autocomplete="off">
            </label>
          </div>
        </price-range>`;
    }

    #activeFilters(facets) {
      const active = [];

      facets.forEach((facet) => {
        if (facet.type === "range") {
          if (this.#state.filters.price) {
            const symbol = this.#settings.currencySymbol;
            const label = `${symbol}${this.#state.priceMin || 0} - ${symbol}${this.#state.priceMax || Math.ceil(facet.max)}`;
            active.push({ facet, label, url: urlFromState(this.#state, { page: 1, filters: { ...this.#state.filters, price: undefined } }) });
          }
          return;
        }

        (facet.options || []).filter((option) => option.status === "selected").forEach((option) => {
          const remaining = (this.#state.filters[facet.name] || []).filter((value) => value !== option.value);
          const filters = { ...this.#state.filters, [facet.name]: remaining };
          active.push({ facet, label: option.display_name, url: urlFromState(this.#state, { page: 1, filters }) });
        });
      });

      return active;
    }

    // Same markup as snippets/active-facets.liquid
    #activeFacetsHtml(facets) {
      const active = this.#activeFilters(facets);
      if (active.length === 0) return "";

      const chips = active.map(({ facet, label, url }) => {
        const text = this.#settings.showGroupName ? `${facet.display_name}: ${label}` : label;
        return `
          <div class="removable-facet">
            <facet-link>
              <a href="${escapeHtml(url)}" class="tap-area link-faded" aria-label="${escapeHtml(this.#settings.strings.removeFilter.replace("__NAME__", label))}" data-no-instant>${this.#icon("close")}</a>
            </facet-link>
            <span class="text-subdued">${escapeHtml(text)}</span>
          </div>`;
      }).join("");

      const clearAll = active.length > 1 ? `
        <facet-link>
          <a href="${escapeHtml(urlFromState(this.#state, { page: 1, filters: {} }))}" class="facets-clear-all text-subdued">
            <span class="link">${escapeHtml(this.#settings.strings.clearFilters)}</span>
          </a>
        </facet-link>` : "";

      return `<div class="active-facets">${chips}${clearAll}</div>`;
    }

    // ── Sort: only the options configured for the index in Constructor's dashboard

    #renderSort(sortOptions) {
      const container = this.querySelector("[data-cnstrc-sort]");
      container.hidden = !this.#settings.showSortBy || sortOptions.length === 0;
      if (container.hidden) return;

      const selected = this.#state.sort;
      const listbox = document.createElement("x-listbox");
      listbox.className = "popover__value-list";
      listbox.setAttribute("data-cnstrc-sort-options", "");
      listbox.innerHTML = sortOptions.map((option) => {
        const value = `${option.sort_by}-${option.sort_order}`;
        const isSelected = selected ? value === selected : option.status === "selected";
        return `
          <button type="button" class="popover__value-option group" role="option" value="${escapeHtml(value)}" ${isSelected ? 'aria-selected="true"' : ""}>
            <span class="reversed-link">${escapeHtml(option.display_name)}</span>
          </button>`;
      }).join("");

      // x-listbox wires its options once, on connect, so swap in a new element
      this.#queryAll("[data-cnstrc-sort-options]")[0]?.replaceWith(listbox);
    }

    // ── Pagination (same markup as snippets/pagination.liquid)

    #paginationHtml(total) {
      const perPage = parseInt(this.getAttribute("per-page"), 10) || 24;
      const pages = Math.ceil(total / perPage);
      const current = this.#state.page;
      if (pages <= 1) return "";

      const strings = this.#settings.strings;
      const link = (page, content, rel) => `
        <facet-link class="contents">
          <a class="pagination__link h6" ${rel ? `rel="${rel}"` : ""} aria-label="${escapeHtml(strings.goToPage.replace("__INDEX__", page))}" href="${escapeHtml(urlFromState(this.#state, { page }))}">${content}</a>
        </facet-link>`;

      let html = current > 1 ? link(current - 1, this.#icon("chevron-left"), "prev") : "";
      let previous = 0;

      for (let page = 1; page <= pages; page++) {
        if (page !== 1 && page !== pages && Math.abs(page - current) > 2) continue;
        if (page - previous > 1) html += `<span class="pagination__link pagination__link--disabled h6">…</span>`;
        html += page === current
          ? `<span class="pagination__link pagination__link--disabled h6" aria-current="page">${page}</span>`
          : link(page, page);
        previous = page;
      }

      if (current < pages) html += link(current + 1, this.#icon("chevron-right"), "next");

      return `<nav class="pagination" role="navigation" aria-label="${escapeHtml(strings.pagination)}">${html}</nav>`;
    }

    #emptyHtml(hasActiveFilters) {
      const strings = this.#settings.strings;

      if (hasActiveFilters) {
        return `
          <div class="empty-state">
            <div class="v-stack gap-4">
              <p>${escapeHtml(strings.noFilterResults)}</p>
              <facet-link>
                <a href="${escapeHtml(urlFromState(this.#state, { page: 1, filters: {} }))}" class="button">${escapeHtml(strings.clearFilters)}</a>
              </facet-link>
            </div>
          </div>`;
      }

      return `
        <div class="empty-state">
          <p>${escapeHtml(strings.noResults.replace("__TERMS__", this.#state.term))}</p>
        </div>`;
    }

    // Constructor unreachable: show Shopify's results for the term (first page, no facets)
    async #renderShopifyFallback(signal) {
      const params = new URLSearchParams({ q: this.#state.term, type: "product", section_id: CARDS_SECTION });
      const doc = await fetchHtml(searchUrl(params), signal);
      const section = doc.querySelector(".shopify-section");
      const cards = Array.from(section?.childNodes || []);
      const total = section ? section.querySelectorAll("product-card").length : 0;

      this.removeAttribute("has-facets");
      this.toggleAttribute("has-results", false);
      this.querySelector("[data-cnstrc-count]").textContent = this.#countText(total);
      this.#queryAll("[data-cnstrc-facets], [data-cnstrc-active-facets], [data-cnstrc-pagination]").forEach((element) => (element.innerHTML = ""));
      this.querySelector("product-list").replaceChildren(...cards);

      const empty = this.querySelector("[data-cnstrc-empty]");
      empty.hidden = total > 0;
      empty.innerHTML = total > 0 ? "" : this.#emptyHtml(false);
    }
  }

  if (!window.customElements.get("cnstrc-search")) {
    window.customElements.define("cnstrc-search", CnstrcSearch);
  }
})();
